import type { RefObject } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import type { ReactPlayerProps } from 'react-player/types';
import BackActive from '@assets/icons/learning-overview/video-player/back-active.svg';
import Maximize from '@assets/icons/learning-overview/video-player/maximize.svg';
import VolumeMute from '@assets/icons/learning-overview/video-player/mute.svg';
import NextDisable from '@assets/icons/learning-overview/video-player/next-disable.svg';
import SettingsActive from '@assets/icons/learning-overview/video-player/settings-active.svg';
import Volume from '@assets/icons/learning-overview/video-player/volume.svg';
import type { SettingsPanelType } from '@components/video-player/constants.ts';
import { Language, LANGUAGES, SettingsPanelItems, SPEEDS } from '@components/video-player/constants.ts';
import {
    createPlayerKeyboardHandler,
    formatTime,
    getAudioTrackLabel,
    getNormalizedDuration,
    getVideoCover,
    performSeek,
    runControlsAutoHide,
} from '@components/video-player/helpers.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useAudioTracks } from '@hooks/useAudioTracks.ts';
import { useClickOutside } from '@hooks/useClickOutside.ts';
import { useFullscreen } from '@hooks/useFullscreen.ts';
import { useLingui } from '@lingui/react/macro';
import { setExpirySessionMediaPlaybackActive } from '@routes/auth/___shared___/sessionExpiryScheduler.ts';
import { setCurrentVideoTime } from '@routes/sidebar/learning/___pages___/learning-overview/model/learningOverview.slice.ts';
import dayjs from 'dayjs';

import {
    BackDisableButton,
    BaseDividerWrapper,
    BigButtonWrapper,
    BigPauseButton,
    BigPlayButton,
    CheckIcon,
    ChevronIcon,
    ControlGroup,
    ControlsOverlay,
    Frame,
    IconWrapper,
    Label,
    MinimizeIcon,
    NextActiveButton,
    PauseSmallButton,
    PlayerContainer,
    PlaySmallButton,
    SeekSlider,
    SeekWrapper,
    SettingsPanel,
    SettingsRow,
    SettingsRowOptions,
    SubtitlesSwitch,
    TextWrapper,
    TimeLabel,
    VideoArea,
    VideoClickOverlay,
    VolumeContainer,
    VolumeSlider,
    WatermarkContainer,
    WatermarkLogo,
    WatermarkMeta,
} from './styled.ts';

interface Props {
    url: string;
    // prevent skipping until user fully watched the video for the first time
    allowSkip: boolean;
    forcePause: boolean;
    cover: string | null;
    durationInSeconds: number;
    allowSpeedChange?: boolean;
    // for trial clients + prevent screenshot
    isDemoUser?: boolean;
    enableSubtitles?: boolean;
    isYouTube?: boolean;
    onVideoEnd?: () => void;
    onVideoStart?: () => void;
    onVideoPlay?: () => void;
    // false - videos in learning, true - admin center elements YT and video preview modal
    isPreviewView?: boolean;
    // audio language codes from the API (e.g. ['en', 'nb']), shown in Settings before the stream is loaded
    audioLanguages?: string[] | null;
}

export const VideoPlayer: React.FC<Props> = ({
    url,
    allowSkip,
    forcePause,
    durationInSeconds,
    allowSpeedChange = true,
    isDemoUser = false,
    enableSubtitles = false,
    isYouTube = false,
    onVideoEnd,
    onVideoStart,
    onVideoPlay,
    cover,
    isPreviewView = true,
    audioLanguages,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null); // container above player
    const playerRef = useRef<HTMLVideoElement | null>(null); // direct video tag/player target for HLS type
    const volumeRef = useRef<HTMLDivElement>(null);
    const settingsRef = useRef<HTMLDivElement>(null);
    const prevVolumeRef = useRef<number>(1); // for M to restore previous volume

    const { t, i18n } = useLingui();
    const dispatch = useAppDispatch();

    const { isFullscreen, toggleFullscreen } = useFullscreen(wrapperRef as RefObject<HTMLElement>);

    const userInfo = useAppSelector((state) => state.user.userInfo?.userPersonalData);

    const [playing, setPlaying] = useState(false);
    const [maxPlayedSeconds, setMaxPlayedSeconds] = useState(0);
    const [showBigOverlay, setShowBigOverlay] = useState(true);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [activePanel, setActivePanel] = useState<SettingsPanelType>(SettingsPanelItems.MAIN);

    const [volume, setVolume] = useState(1);
    const [volumeMenuOpen, setVolumeMenuOpen] = useState(false);

    const [subtitles, setSubtitles] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(Language.ENGLISH);

    // media element in state, so the audio tracks hook can subscribe when the player mounts
    const [mediaElement, setMediaElement] = useState<HTMLVideoElement | null>(null);
    const { audioTracks, selectedAudioTrackId, selectAudioTrack } = useAudioTracks(
        mediaElement,
        i18n.locale,
        audioLanguages,
    );
    const selectedAudioTrack = audioTracks.find((track) => track.id === selectedAudioTrackId);
    const hasAudioTrackChoice = audioTracks.length > 1;

    // true while the user is actively dragging the seek slider (“scrubbing” the timeline)
    const [isScrubbing, setIsScrubbing] = useState(false);
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [duration, setDuration] = useState(durationInSeconds);

    const [canSkip, setCanSkip] = useState(allowSkip);
    const [now, setNow] = useState(dayjs());

    const [coverUrl, setCoverUrl] = useState<string | null>(cover);

    const playVideoClickedRef = useRef(false);
    const resumePositionRef = useRef<number | null>(null);
    const previousUrlRef = useRef(url);

    // Auto-hide controls
    const [controlsVisible, setControlsVisible] = useState(true);
    const hideControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const CURRENT_TIME = now.format('DD.MM.YYYY HH:mm:ss');
    const SKIP_SECONDS = 5;

    const youtubeConfig: ReactPlayerProps['config'] = {
        youtube: {
            enablejsapi: 1,
            disablekb: 1,
            origin: window.location.origin,
            rel: 0,
        },
    };

    const seekProps = {
        isYouTube,
        wrapperRef,
        playerRef,
        duration,
        setPlayedSeconds,
    };

    // Close volume menu on click outside
    useClickOutside(volumeRef as RefObject<HTMLElement>, () => {
        if (volumeMenuOpen) setVolumeMenuOpen(false);
    });

    const closeSettings = () => setSettingsOpen(false);

    const getSettingsPanelTitle = (panel: SettingsPanelType) => {
        if (panel === SettingsPanelItems.SPEED) return t`Speed`;
        if (panel === SettingsPanelItems.LANGUAGES) return t`Languages`;
        if (panel === SettingsPanelItems.AUDIO) return t`Audio`;
        return panel;
    };

    const handlePlayerRef = useCallback((node: HTMLVideoElement | null) => {
        const previousMedia = playerRef.current;

        // Stop previous video playback when the media element is detached or replaced
        if (previousMedia && previousMedia !== node) {
            previousMedia.pause();
        }

        playerRef.current = node;
        setMediaElement(node);
    }, []);

    // Close settings menu on click outside
    useClickOutside(settingsRef as RefObject<HTMLElement>, () => {
        if (settingsOpen) closeSettings();
    });

    // Update current time in watermark
    useEffect(() => {
        const timer = setInterval(() => {
            setNow(dayjs());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    //force pause when isShareFeedbackModalOpened
    useEffect(() => {
        if (forcePause) {
            dispatch(setCurrentVideoTime(Math.floor(playedSeconds % 60)));
            setPlaying(false);
        }
    }, [forcePause]);

    // Auto-hide controls
    useEffect(() => {
        return runControlsAutoHide({
            playing,
            settingsOpen,
            volumeMenuOpen,
            isScrubbing,
            setControlsVisible,
            hideControlsTimeout,
        });
    }, [playing, settingsOpen, volumeMenuOpen, isScrubbing]);

    // Preserve playback position when the temporary video URL is refreshed
    useEffect(() => {
        if (previousUrlRef.current !== url && playedSeconds > 0 && resumePositionRef.current === null) {
            resumePositionRef.current = playedSeconds;
        }

        previousUrlRef.current = url;
    }, [url]);

    useEffect(() => {
        setExpirySessionMediaPlaybackActive(playing);

        return () => {
            setExpirySessionMediaPlaybackActive(false);
        };
    }, [playing]);

    // Ensure video playback stops when the component unmounts
    useEffect(() => {
        return () => {
            playerRef.current?.pause();
        };
    }, []);

    const seekWithinPermissions = (rawTarget: number) => {
        const maxAllowed = canSkip ? duration : maxPlayedSeconds;
        const target = Math.min(rawTarget, maxAllowed);
        performSeek({ target, ...seekProps });
        setMaxPlayedSeconds((m) => (target > m ? target : m));
    };

    const toggleVolumeMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setVolumeMenuOpen((v) => !v);
    };

    const toggleSettings = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSettingsOpen((s) => !s);
        setActivePanel(SettingsPanelItems.MAIN);
    };

    useEffect(() => {
        if (playing && !playVideoClickedRef.current) {
            playVideoClickedRef.current = true;
            onVideoStart?.();
        }
    }, [playing, onVideoStart]);

    const togglePlay = () => {
        if (!playing) {
            onVideoPlay?.();
        }

        setPlaying((prev) => !prev);
    };

    // Smoothly hide big overlay
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (playing) {
            setShowBigOverlay(true);
            timeout = setTimeout(() => setShowBigOverlay(false), 500);
        } else {
            setShowBigOverlay(true);
        }
        return () => clearTimeout(timeout);
    }, [playing]);

    const handleEnded = () => {
        setPlaying(false);
        if (!canSkip) {
            setCanSkip(true);
        }
        onVideoEnd?.();
    };

    const doSeek = (target: number) => seekWithinPermissions(target);
    const handleSeekSlider: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const ratio = parseFloat(e.currentTarget.value);
        const rawTarget = ratio * duration;
        doSeek(rawTarget);
    };

    const handleSkipForward = (seconds = SKIP_SECONDS) => {
        if (!canSkip) return;
        doSeek(Math.min(duration, playedSeconds + seconds));
    };

    const handleSkipBack = (seconds = SKIP_SECONDS) => {
        if (playedSeconds <= 0) return;
        doSeek(Math.max(0, playedSeconds - seconds));
    };

    const handleTimeUpdate: ReactPlayerProps['onTimeUpdate'] = (e) => {
        const time = e.currentTarget.currentTime;
        const mediaDuration = getNormalizedDuration(e.currentTarget.duration);

        setPlayedSeconds(time);

        if (mediaDuration && mediaDuration !== duration) {
            setDuration(mediaDuration);
        }

        if (time > maxPlayedSeconds) {
            setMaxPlayedSeconds(time);
        }
    };

    const handleDurationChange: ReactPlayerProps['onDurationChange'] = (e) => {
        const mediaDuration = getNormalizedDuration(e.currentTarget.duration);

        if (mediaDuration) {
            setDuration(mediaDuration);
        }
    };

    const handleScrubbing = () => setIsScrubbing(true);

    // Restores the previous playback position after the video URL is refreshed
    const handleLoadedMetadata: ReactPlayerProps['onLoadedMetadata'] = (event) => {
        if (isYouTube || resumePositionRef.current === null) {
            return;
        }

        const mediaDuration = getNormalizedDuration(event.currentTarget.duration);
        const resumePosition = mediaDuration
            ? Math.min(resumePositionRef.current, mediaDuration)
            : resumePositionRef.current;

        event.currentTarget.currentTime = resumePosition;
        setPlayedSeconds(resumePosition);

        resumePositionRef.current = null;
    };

    // --- KEYBOARD CONTROLS ---
    const onKeyDown = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
        (event) => {
            const handleKeyDown = createPlayerKeyboardHandler({
                settingsOpen,
                volumeMenuOpen,
                canSkip,
                allowSpeedChange,
                enableSubtitles,
                duration,
                volume,
                setVolume,
                prevVolumeRef,
                playbackRate,
                setPlaybackRate,
                togglePlay,
                toggleFullscreen,
                toggleSubtitles: () => setSubtitles((s) => !s),
                skipForward: handleSkipForward,
                skipBack: handleSkipBack,
                seekTo: seekWithinPermissions,
                speeds: SPEEDS,
            });

            handleKeyDown(event);
        },
        [
            settingsOpen,
            volumeMenuOpen,
            canSkip,
            allowSpeedChange,
            enableSubtitles,
            duration,
            volume,
            playbackRate,
            toggleFullscreen,
            handleSkipForward,
            handleSkipBack,
            seekWithinPermissions,
        ],
    );

    const showPlayer = playing || playedSeconds >= 0.1;

    // Auto-hide controls
    const revealControls = () => {
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current);
            hideControlsTimeout.current = null;
        }
        setControlsVisible(true);
        if (playing && !settingsOpen && !volumeMenuOpen && !isScrubbing) {
            hideControlsTimeout.current = setTimeout(() => {
                setControlsVisible(false);
            }, 2500);
        }
    };

    const showControls = () => {
        return !playing || controlsVisible || settingsOpen || volumeMenuOpen || isScrubbing;
    };

    useEffect(() => {
        getVideoCover(isYouTube, cover, url)
            .then((coverUrl) => {
                setCoverUrl(coverUrl);
            })
            .catch((err) => {
                console.error('Failed to load video cover:', err);
            });
    }, [isYouTube, cover, url]);

    return (
        <PlayerContainer
            ref={wrapperRef}
            tabIndex={0}
            onKeyDown={onKeyDown}
            $isFullScreen={isFullscreen}
            onMouseMove={revealControls}
            onMouseEnter={revealControls}
            onMouseLeave={() => {
                if (playing) setControlsVisible(false);
            }}
            onTouchStart={revealControls}
        >
            <VideoArea $isFullScreen={isFullscreen}>
                {!!showPlayer && (
                    <ReactPlayer
                        ref={handlePlayerRef}
                        slot="media"
                        src={url}
                        playing={playing}
                        controls={false}
                        config={isYouTube ? youtubeConfig : {}}
                        volume={volume}
                        muted={volume === 0}
                        playbackRate={playbackRate}
                        width="100%"
                        height="100%"
                        onTimeUpdate={handleTimeUpdate}
                        onDurationChange={handleDurationChange}
                        onEnded={handleEnded}
                        onLoadedMetadata={handleLoadedMetadata}
                    />
                )}

                {/* clickable overlay */}
                <VideoClickOverlay
                    onClick={togglePlay}
                    $showFallbackBackground={playedSeconds === 0}
                    $cover={coverUrl}
                />

                <BigButtonWrapper $visible={!playing} onClick={togglePlay}>
                    <BigPlayButton />
                </BigButtonWrapper>

                <BigButtonWrapper $visible={playing ? showBigOverlay : false} onClick={togglePlay}>
                    <BigPauseButton />
                </BigButtonWrapper>

                {!!(isDemoUser && playing && userInfo) && (
                    <BigButtonWrapper $visible={isDemoUser}>
                        <WatermarkContainer>
                            <WatermarkLogo />
                            <WatermarkMeta>
                                {userInfo.firstName} {userInfo.lastName}
                            </WatermarkMeta>
                            <WatermarkMeta>{userInfo.email}</WatermarkMeta>
                            <WatermarkMeta>{CURRENT_TIME}</WatermarkMeta>
                        </WatermarkContainer>
                    </BigButtonWrapper>
                )}

                {showControls() && (
                    <ControlsOverlay>
                        <ControlGroup>
                            <IconWrapper onClick={togglePlay}>
                                {playing ? (
                                    <PauseSmallButton $isPreviewView={isPreviewView} />
                                ) : (
                                    <PlaySmallButton $isPreviewView={isPreviewView} />
                                )}
                            </IconWrapper>

                            <IconWrapper disabled={playedSeconds <= 0} onClick={() => handleSkipBack(SKIP_SECONDS)}>
                                {playedSeconds <= 0 ? <BackDisableButton /> : <BackActive />}
                            </IconWrapper>
                            <IconWrapper
                                disabled={!canSkip || playedSeconds >= duration}
                                onClick={() => handleSkipForward(SKIP_SECONDS)}
                            >
                                {canSkip ? <NextActiveButton /> : <NextDisable />}
                            </IconWrapper>
                        </ControlGroup>

                        <SeekWrapper>
                            <SeekSlider
                                $played={duration ? playedSeconds / duration : 0}
                                $watched={duration ? maxPlayedSeconds / duration : 0}
                                $isScrubbing={isScrubbing}
                                value={duration ? playedSeconds / duration : 0}
                                onChange={handleSeekSlider}
                                onMouseDown={handleScrubbing}
                                onMouseUp={() => setIsScrubbing(false)}
                                onTouchStart={handleScrubbing}
                                onTouchEnd={() => setIsScrubbing(false)}
                            />
                            <TimeLabel>{`${formatTime(playedSeconds)} / ${formatTime(duration)}`}</TimeLabel>
                        </SeekWrapper>

                        <ControlGroup>
                            <VolumeContainer ref={volumeRef}>
                                <IconWrapper onClick={toggleVolumeMenu}>
                                    {volume === 0 ? <VolumeMute /> : <Volume />}
                                </IconWrapper>

                                {!!volumeMenuOpen && (
                                    <Frame onClick={(e) => e.stopPropagation()}>
                                        <VolumeSlider
                                            type="range"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={volume}
                                            onChange={(e) => setVolume(parseFloat(e.target.value))}
                                        />
                                    </Frame>
                                )}
                            </VolumeContainer>
                            <IconWrapper onClick={toggleSettings}>
                                <SettingsActive />
                            </IconWrapper>
                            <IconWrapper onClick={toggleFullscreen}>
                                {isFullscreen ? <MinimizeIcon /> : <Maximize />}
                            </IconWrapper>
                        </ControlGroup>
                    </ControlsOverlay>
                )}

                {!!settingsOpen && (
                    <SettingsPanel ref={settingsRef}>
                        {activePanel !== SettingsPanelItems.MAIN && (
                            <>
                                <SettingsRow isBack isSubMenu onClick={() => setActivePanel(SettingsPanelItems.MAIN)}>
                                    <ChevronIcon $rotated />
                                    <Label>{getSettingsPanelTitle(activePanel)}</Label>
                                </SettingsRow>
                                <BaseDividerWrapper />
                            </>
                        )}

                        {activePanel === SettingsPanelItems.MAIN && (
                            <>
                                {!!enableSubtitles && (
                                    <SettingsRow onClick={() => setSubtitles(!subtitles)}>
                                        <Label>{t`Subtitles`}</Label>
                                        <SubtitlesSwitch isOn={subtitles}>
                                            <div className="knob" />
                                        </SubtitlesSwitch>
                                    </SettingsRow>
                                )}

                                <SettingsRow onClick={() => setActivePanel(SettingsPanelItems.SPEED)}>
                                    <Label>{t`Speed`}</Label>
                                    <SettingsRowOptions>
                                        <TextWrapper>{playbackRate}x</TextWrapper>
                                        <ChevronIcon />
                                    </SettingsRowOptions>
                                </SettingsRow>

                                {!!enableSubtitles && (
                                    <SettingsRow onClick={() => setActivePanel(SettingsPanelItems.LANGUAGES)}>
                                        <Label>{t`Languages`}</Label>
                                        <SettingsRowOptions>
                                            <TextWrapper>{selectedLanguage}</TextWrapper>
                                            <ChevronIcon />
                                        </SettingsRowOptions>
                                    </SettingsRow>
                                )}

                                {!!hasAudioTrackChoice && (
                                    <SettingsRow onClick={() => setActivePanel(SettingsPanelItems.AUDIO)}>
                                        <Label>{t`Audio`}</Label>
                                        <SettingsRowOptions>
                                            <TextWrapper>
                                                {getAudioTrackLabel(selectedAudioTrack ?? audioTracks[0])}
                                            </TextWrapper>
                                            <ChevronIcon />
                                        </SettingsRowOptions>
                                    </SettingsRow>
                                )}
                            </>
                        )}

                        {activePanel === SettingsPanelItems.SPEED &&
                            SPEEDS.map((speed) => (
                                <SettingsRow
                                    isSubMenu
                                    key={speed}
                                    onClick={() => {
                                        setPlaybackRate(speed);
                                        closeSettings();
                                    }}
                                >
                                    {playbackRate === speed && <CheckIcon />}
                                    <Label>{speed}x</Label>
                                </SettingsRow>
                            ))}

                        {activePanel === SettingsPanelItems.LANGUAGES &&
                            LANGUAGES.map((lang) => (
                                <SettingsRow
                                    isSubMenu
                                    key={lang}
                                    onClick={() => {
                                        setSelectedLanguage(lang);
                                        closeSettings();
                                    }}
                                >
                                    {selectedLanguage === lang && <CheckIcon />}
                                    <Label>{lang}</Label>
                                </SettingsRow>
                            ))}

                        {activePanel === SettingsPanelItems.AUDIO &&
                            audioTracks.map((track) => (
                                <SettingsRow
                                    isSubMenu
                                    key={track.id}
                                    onClick={() => {
                                        selectAudioTrack(track);
                                        closeSettings();
                                    }}
                                >
                                    {selectedAudioTrackId === track.id && <CheckIcon />}
                                    <Label>{getAudioTrackLabel(track)}</Label>
                                </SettingsRow>
                            ))}
                    </SettingsPanel>
                )}
            </VideoArea>
        </PlayerContainer>
    );
};
